import {
	Command,
	createElementSelectionResult,
	type CommandResult,
} from "@/commands/base-command";
import type { SceneTracks, TimelineElement, TransitionType } from "@/timeline";
import { generateUUID } from "@/utils/id";
import { EditorCore } from "@/core";
import { getSourceSpanAtClipTime } from "@/retime";
import { type MediaTime, roundMediaTime } from "@/wasm";

/**
 * Add a transition (B4) into a clip from its left neighbour at the cut.
 *
 * Stores a `transition` record on the RIGHT clip; the renderer synthesizes the
 * seam (scene-builder.buildTransitionNodes). The duration is clamped to fit both
 * clips and the right clip's trim headroom (the incoming side samples footage
 * before its in-point). Mirrors FreezeFrameCommand: snapshot tracks, set the
 * field, updateTracks; undo restores the snapshot.
 */
export class AddTransitionCommand extends Command {
	private savedState: SceneTracks | null = null;
	private readonly target: { trackId: string; elementId: string };
	private readonly type: TransitionType;
	private readonly requestedDuration: MediaTime;

	constructor({
		element,
		type,
		duration,
	}: {
		element: { trackId: string; elementId: string };
		type: TransitionType;
		duration: MediaTime;
	}) {
		super();
		this.target = element;
		this.type = type;
		this.requestedDuration = duration;
	}

	execute(): CommandResult | undefined {
		const editor = EditorCore.getInstance();
		const tracks = editor.scenes.getActiveScene().tracks;

		const track = [tracks.main, ...tracks.overlay, ...tracks.audio].find(
			(candidate) => candidate.id === this.target.trackId,
		);
		if (!track) {
			return undefined;
		}
		const element = track.elements.find(
			(candidate) => candidate.id === this.target.elementId,
		);
		if (!element || element.type !== "video") {
			return undefined;
		}

		// Left neighbour must be a video clip that touches the cut.
		const sorted = track.elements
			.slice()
			.sort((a, b) => a.startTime - b.startTime);
		const index = sorted.findIndex(
			(candidate) => candidate.id === this.target.elementId,
		);
		const left = index > 0 ? sorted[index - 1] : null;
		if (!left || left.type !== "video") {
			return undefined;
		}
		if (left.startTime + left.duration !== element.startTime) {
			return undefined;
		}

		// Clamp to fit both clips, then to the right clip's trim headroom.
		let duration = Math.min(
			this.requestedDuration,
			left.duration,
			element.duration,
		);
		if (duration > 0) {
			const headSpan = roundMediaTime({
				time: getSourceSpanAtClipTime({
					clipTime: duration,
					retime: element.retime,
				}),
			});
			if (headSpan > element.trimStart) {
				duration =
					headSpan > 0
						? Math.round(duration * (element.trimStart / headSpan))
						: 0;
			}
		}
		if (duration <= 0) {
			return undefined;
		}

		this.savedState = tracks;
		const transition = {
			id: generateUUID(),
			type: this.type,
			duration: duration as MediaTime,
		};

		const updatedElements: TimelineElement[] = track.elements.map((candidate) =>
			candidate.id === this.target.elementId && candidate.type === "video"
				? { ...candidate, transition }
				: candidate,
		);
		const updatedTrack = { ...track, elements: updatedElements };

		const updatedTracks: SceneTracks = {
			main: tracks.main.id === track.id ? updatedTrack : tracks.main,
			overlay: tracks.overlay.map((t) =>
				t.id === track.id ? updatedTrack : t,
			),
			audio: tracks.audio.map((t) => (t.id === track.id ? updatedTrack : t)),
		} as SceneTracks;

		editor.timeline.updateTracks(updatedTracks);
		return createElementSelectionResult([this.target]);
	}

	undo(): void {
		if (this.savedState) {
			const editor = EditorCore.getInstance();
			editor.timeline.updateTracks(this.savedState);
		}
	}
}
